import { useContext, useEffect, useRef, useState } from 'react';
import { CHIP_LABELS, formatPrice, type AnalysisResult, type Objective } from '@fantasy/types';
import { SquadContext } from '../state/SquadProvider.tsx';
import { ShareVerdict, shareSummary, shareUrl } from './ShareVerdict.tsx';
import { Term } from './Term.tsx';

interface AnalysisViewProps {
  analysis: AnalysisResult;
  /**
   * The FPL entry id behind this squad, when the caller has one — a signed-
   * in manager's `Squad.entryId` is public FPL data, same as the entry id a
   * signed-out `/try` verdict's `squadId` already encodes (see `shareUrl`'s
   * own comment). Passing it here is what gives that manager's own squad a
   * share link too, not just the free-preview one. Omit it for a squad with
   * no linked FPL entry — the copy summary still works either way; it also
   * falls back to `SquadContext` below, so most callers never need to pass
   * this explicitly at all.
   */
  entryId?: number;
}

/** Matches the wording offered by SquadView's own objective control, so the
 * verdict names the choice the same way the manager picked it, not by the
 * API's enum value. */
const OBJECTIVE_LABELS: Record<Objective, string> = {
  climb: 'chasing a rank',
  protect: 'protecting a rank',
  balanced: 'straight points',
};

export function AnalysisView({ analysis, entryId }: AnalysisViewProps) {
  const { captain, transfers, chip, assessments, warnings } = analysis;
  const ranked = [...assessments].sort((a, b) => b.score - a.score);

  // This renders below a squad that can easily run a full screen tall —
  // "Analyse my squad" is a fair way up the page, so a result that just
  // silently appears off-screen is a result nobody finds without knowing to
  // scroll. Focusing the heading brings it into view for a sighted user (a
  // focused element scrolls into view by default) and announces it to a
  // screen reader at the same time — one mechanism, not two. Re-fires on
  // every fresh analysis, not just the first, since a re-run is exactly as
  // easy to lose track of as the initial one.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [analysis]);

  // "Copied" resets on its own timer rather than on the next click, so a
  // manager who copies the summary then the link (or the reverse) sees each
  // confirmation in turn instead of one silently overwriting the other.
  const [copyStatus, setCopyStatus] = useState<'summary' | 'link' | null>(null);
  // Off by default: the card repeats the verdict, captain and reasoning
  // above it verbatim so it stands alone as a screenshot — mounting it
  // unconditionally would mean every one of those appearing twice on a page
  // nobody asked to duplicate. It only exists once a manager actually wants
  // something to screenshot.
  const [showCard, setShowCard] = useState(false);
  // `entryId` is only ever passed explicitly by `PublicVerdict` — the signed-
  // in call sites (`SquadRoute`, `ThisWeek`) render the loaded squad's own
  // verdict without threading it through, so this falls back to the same
  // `Squad.entryId` sitting in `SquadContext`. `useContext` directly, not a
  // throwing `useSquad()`, so this still works for `PublicVerdict`'s /try
  // page and any test that renders `AnalysisView` with no `SquadProvider`
  // ancestor at all — same reasoning `SquadProvider` itself uses for
  // `AuthContext`.
  const contextEntryId = useContext(SquadContext)?.squad?.entryId;
  const link = shareUrl(analysis, entryId ?? contextEntryId);

  async function copy(text: string, which: 'summary' | 'link') {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(which);
      setTimeout(() => setCopyStatus((current) => (current === which ? null : current)), 2000);
    } catch {
      // Clipboard access can be denied (permissions, insecure context) —
      // there's nothing more to do here than leave the button unconfirmed;
      // the text is still selectable/visible for a manual copy.
    }
  }

  return (
    <section className="panel panel--accent rise-in" aria-labelledby="verdict-heading">
      <h3 id="verdict-heading" ref={headingRef} tabIndex={-1}>
        The verdict
      </h3>
      <p className="hint">
        Gameweek {analysis.gameweek} · generated by {analysis.model}
        <> · optimised for {OBJECTIVE_LABELS[analysis.objective]}</>
      </p>

      {/* T-077's reason — the standing that picked the objective when it was
       * chosen automatically, or that a manager set it themselves. This is
       * the sentence that makes the verdict arguable rather than oracular, so
       * it reads as a line of reasoning next to the objective it explains,
       * not as a debug field. */}
      <p className="callout-detail">{analysis.objectiveReason}</p>

      {warnings.length > 0 && (
        <ul className="notices">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}

      <blockquote className="verdict">
        <p>{analysis.summary}</p>
      </blockquote>

      <dl className="callout">
        <dt>Captain</dt>
        <dd>
          <strong>{captain.pick}</strong>
          {captain.alternative && <span> — or {captain.alternative} if you want the safer route</span>}
        </dd>
        <dd className="callout-detail">{captain.reasoning}</dd>
      </dl>

      <dl className="callout">
        <dt>Transfers</dt>
        {transfers.recommended.length === 0 ? (
          <dd className="text-dim">
            {transfers.holdReasoning ?? 'Hold — nothing is worth the move this week.'}
          </dd>
        ) : (
          <>
            {transfers.recommended.map((transfer) => (
              <dd key={`${transfer.out}-${transfer.in}`} className="transfer-entry">
                <strong>{transfer.out}</strong> → <strong>{transfer.in}</strong>{' '}
                <span className="text-faint">
                  ({transfer.netCost >= 0 ? '' : '+'}
                  {formatPrice(Math.abs(transfer.netCost))}, {transfer.confidence} confidence)
                </span>
                <div className="callout-detail">{transfer.reasoning}</div>
                {/* T-116 — alternatives are only ever attached when a real
                 * threshold in code found something close (see
                 * CLOSE_ALTERNATIVE_THRESHOLD in analysis.ts); the model
                 * never decides this, and a clear-cut pick has no
                 * `alternatives` at all, so this block simply doesn't render
                 * and the entry reads exactly as it always has. */}
                {transfer.alternatives && transfer.alternatives.length > 0 && (
                  <div className="callout-detail">
                    Too close to call — {transfer.in} only just beat{' '}
                    {transfer.alternatives.map((alt, i, all) => (
                      <span key={alt.name}>
                        {i > 0 && (i === all.length - 1 ? ' and ' : ', ')}
                        {alt.name} ({alt.club}, {formatPrice(alt.price)}, score {alt.score})
                      </span>
                    ))}
                    .
                  </div>
                )}
              </dd>
            ))}
            {transfers.pointsHit > 0 && (
              <dd className="text-warn">
                Costs a {transfers.pointsHit}-point <Term term="hit">hit</Term>.
              </dd>
            )}
          </>
        )}
      </dl>

      <dl className="callout">
        <dt>Chip</dt>
        <dd>
          <strong>{chip.recommended ? CHIP_LABELS[chip.recommended] : 'Hold them all'}</strong>
        </dd>
        <dd className="callout-detail">{chip.reasoning}</dd>
      </dl>

      {/* T-089 — the share affordance. The copy summary works for every
       * verdict; the link only exists when this analysis has a public FPL
       * entry behind it — either the `entryId` prop a caller passed in for a
       * linked squad, or the one a signed-out `/try` verdict's `squadId`
       * already encodes (see `shareUrl`'s own comment) — so it's omitted
       * rather than shown broken for a manually-entered squad. */}
      <div className="share-actions">
        <button type="button" className="ghost small" onClick={() => copy(shareSummary(analysis), 'summary')}>
          {copyStatus === 'summary' ? 'Copied!' : 'Copy summary'}
        </button>
        {link && (
          <button type="button" className="ghost small" onClick={() => copy(link, 'link')}>
            {copyStatus === 'link' ? 'Copied!' : 'Copy link'}
          </button>
        )}
        <button type="button" className="ghost small" aria-expanded={showCard} onClick={() => setShowCard((v) => !v)}>
          {showCard ? 'Hide shareable card' : 'Show shareable card'}
        </button>
      </div>

      {showCard && <ShareVerdict analysis={analysis} />}

      <div className="scroll-x">
        <table>
          <caption className="hint table-caption">
            Every player, ranked by the model's 0–100 score
          </caption>
          <thead>
            <tr>
              <th scope="col">Player</th>
              <th scope="col">Pos</th>
              <th scope="col">Club</th>
              <th scope="col" className="num">
                Price
              </th>
              <th scope="col" className="num">
                Form
              </th>
              <th scope="col" className="num">
                Fixtures
              </th>
              <th scope="col" className="num">
                Score
              </th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((player) => (
              <tr key={player.name}>
                <td>
                  {player.name}
                  {player.flags.map((flag) => (
                    <span key={flag} className="pill">
                      {flag.replace('_', ' ')}
                    </span>
                  ))}
                </td>
                <td>{player.position}</td>
                <td>{player.club}</td>
                <td className="num">{formatPrice(player.price)}</td>
                <td className="num">{player.form.toFixed(1)}</td>
                <td className="num">{player.fixtureOutlook.toFixed(1)}</td>
                <td className="num">{player.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
